import { HeadData } from '@/components/HeadData';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { useSidebar } from '@/routes/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import Page from '../-Page';
import { useOverwriteReflectionMutation } from '../-hooks/useOverwriteReflectionMutation';
import { useEvaluationPolling } from '../../-hooks/useEvaluationPolling';
import { COURSE_LESSON_DETAIL_FRAGMENT } from '../../index.route.lazy';

export const REFLECTION_RESULT_FRAGMENT = graphql(/* GraphQL */ `
  fragment ReflectionResult on UserReflectionResult {
    id
    isSummarized
    reflection
    overwriteBlockReason
    embaCourse {
      name
    }
  }
`);

export type ReflectionResultContainerProps = {
  reflectionResult: FragmentType<typeof REFLECTION_RESULT_FRAGMENT>;
  userAttendanceSlug: string;
  lessonSlug: string;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
  isTimeout: boolean;
  courseLesson: FragmentType<typeof COURSE_LESSON_DETAIL_FRAGMENT>;
};

export default function ReflectionResultContainer({
  reflectionResult,
  userAttendanceSlug,
  lessonSlug,
  startPolling,
  stopPolling,
  isTimeout,
  courseLesson,
}: ReflectionResultContainerProps) {
  const { showSidebar } = useSidebar();
  useEffect(() => {
    showSidebar();
  }, [showSidebar]);
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedReflection, setEditedReflection] = useState('');
  const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(isTimeout);

  const reflectionResultData = useFragment(
    REFLECTION_RESULT_FRAGMENT,
    reflectionResult,
  );

  const courseLessonData = useFragment(
    COURSE_LESSON_DETAIL_FRAGMENT,
    courseLesson,
  );

  useEvaluationPolling(
    reflectionResultData.isSummarized,
    startPolling,
    stopPolling,
  );

  const {
    overwriteReflection,
    overwriteReflectionMutationError,
    overwriteReflectionMutationLoading,
  } = useOverwriteReflectionMutation(userAttendanceSlug, lessonSlug);

  if (overwriteReflectionMutationError) {
    throw overwriteReflectionMutationError;
  }

  const onOpenModal = () => {
    setIsEditModalOpen(true);
    setEditedReflection(reflectionResultData.reflection || '');
  };

  const onCloseModal = () => {
    setIsEditModalOpen(false);
    setEditedReflection('');
  };

  const onOverwriteReflection = async () => {
    await overwriteReflection(editedReflection);
    setIsEditModalOpen(false);
    setEditedReflection('');
  };

  const onNext = () => {
    navigate({
      to: '/user-attendances/$userAttendanceSlug/lessons/$lessonSlug/complete',
      params: {
        userAttendanceSlug,
        lessonSlug,
      },
    });
  };

  const headTitle = reflectionResultData.embaCourse.name;

  return (
    <>
      <HeadData title={headTitle} />
      <Page
        onOpenModal={onOpenModal}
        onCloseModal={onCloseModal}
        onOverwriteReflection={onOverwriteReflection}
        onNext={onNext}
        isSummarized={reflectionResultData.isSummarized}
        isOverwriting={overwriteReflectionMutationLoading}
        overwriteBlockReason={reflectionResultData.overwriteBlockReason}
        reflection={reflectionResultData.reflection}
        isEditModalOpen={isEditModalOpen}
        editedReflection={editedReflection}
        setEditedReflection={setEditedReflection}
        isTimeoutModalOpen={isTimeoutModalOpen}
        setIsTimeoutModalOpen={setIsTimeoutModalOpen}
        chapterName={courseLessonData.embaCourseChapter.name}
        lessonName={courseLessonData.name}
      />
    </>
  );
}
